import { useState } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useSearchFsnbCoefficientsQuery,
  useSearchFsnbResourcesQuery,
  useSearchFsnbWorksQuery,
  type FsnbEdition,
} from '@shared/api';
import { fmtNum } from '@shared/lib/smetaLabels';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Input, Select } from '@shared/ui/Input';
import { CoefficientCardDrawer, ResourceCardDrawer, WorkCardDrawer } from './NormBaseCards';
import {
  BASE_TYPES,
  BASE_TYPE_TITLES,
  PRICE_SOURCES,
  coefDecreeText,
  editionStatusLabel,
  fullCode,
  priceText,
  sourceLabel,
} from './normBaseLabels';

const PAGE = 50;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Toggle = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: none;
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  font: inherit;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
`;

const SearchInput = styled(Input)`
  flex: 1 1 320px;
  min-width: 220px;
`;

const NarrowSelect = styled(Select)`
  flex: 0 1 220px;
  width: auto;
`;

const Grid = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-weight: 600;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.surface};
  }

  td.code {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }

  tbody tr {
    cursor: pointer;
  }

  tbody tr:hover td {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Note = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.5;
`;

const Err = styled(Note)`
  color: ${({ theme }) => theme.colors.status.danger};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  & .grow {
    flex: 1;
  }
`;

type Mode = 'works' | 'resources' | 'coefficients';

const PLACEHOLDERS: Record<Mode, string> = {
  works: 'Шифр (ГЭСНм 08-02-401-01, 06-01-001) или слова наименования',
  resources: 'Шифр (01.7.15.06-0111, 91.01.01-014) или слова наименования',
  coefficients: 'Код (3.230, 2022-18-ГЭСН-ПР-01-12) или слова: стеснённые условия',
};

/** Вкладка «Поиск»: нормы ГЭСН, ресурсы справочников цен и поправочные
 *  коэффициенты выбранной редакции. */
export function NormBaseSearch({
  editions,
  activeEdition,
}: {
  editions: FsnbEdition[];
  activeEdition: string | null;
}) {
  const [mode, setMode] = useState<Mode>('works');
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  const [edition, setEdition] = useState('');
  const [page, setPage] = useState(0);
  const [openWork, setOpenWork] = useState<{ baseType: string; code: string } | null>(null);
  const [openResource, setOpenResource] = useState<{ source: string; code: string } | null>(null);
  const [openCoefficient, setOpenCoefficient] = useState<string | null>(null);
  const q = useDebouncedValue(query.trim(), 300);

  const shownEdition = edition || activeEdition || '';
  const browsable = editions.filter((e) => e.status !== 'importing' && e.status !== 'failed');
  const editionTitle = editions.find((e) => e.edition === shownEdition)?.title;
  const ready = Boolean(shownEdition) && (q.length > 0 || kind !== '');
  const args = {
    q,
    kind: kind || undefined,
    edition: edition || undefined,
    limit: PAGE,
    offset: page * PAGE,
  };
  const works = useSearchFsnbWorksQuery(args, { skip: !ready || mode !== 'works' });
  const resources = useSearchFsnbResourcesQuery(args, { skip: !ready || mode !== 'resources' });
  const coefficients = useSearchFsnbCoefficientsQuery(args, {
    skip: !ready || mode !== 'coefficients',
  });
  const current = mode === 'works' ? works : mode === 'resources' ? resources : coefficients;
  const coefficientsMissing =
    mode === 'coefficients' && coefficients.data !== undefined && !coefficients.data.loaded;
  const totalFound = current.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(totalFound / PAGE));

  function switchMode(next: Mode) {
    setMode(next);
    setKind('');
    setPage(0);
  }

  return (
    <Card>
      <Controls>
        <Toggle role="group" aria-label="Что искать">
          <ToggleBtn type="button" $active={mode === 'works'} onClick={() => switchMode('works')}>
            Нормы ГЭСН
          </ToggleBtn>
          <ToggleBtn
            type="button"
            $active={mode === 'resources'}
            onClick={() => switchMode('resources')}
          >
            Ресурсы и цены
          </ToggleBtn>
          <ToggleBtn
            type="button"
            $active={mode === 'coefficients'}
            onClick={() => switchMode('coefficients')}
          >
            Поправочные коэффициенты
          </ToggleBtn>
        </Toggle>
        <SearchInput
          type="search"
          value={query}
          placeholder={PLACEHOLDERS[mode]}
          aria-label="Поиск"
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
        />
        <NarrowSelect
          value={kind}
          aria-label={mode === 'resources' ? 'Сборник' : 'Вид базы'}
          onChange={(e) => {
            setKind(e.target.value);
            setPage(0);
          }}
        >
          {mode !== 'resources' ? (
            <>
              <option value="">Все виды базы</option>
              {BASE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t} — {BASE_TYPE_TITLES[t]}
                </option>
              ))}
            </>
          ) : (
            <>
              <option value="">Все сборники</option>
              {PRICE_SOURCES.map((s) => (
                <option key={s.value} value={s.value} title={s.hint}>
                  {s.label}
                </option>
              ))}
            </>
          )}
        </NarrowSelect>
        {browsable.length > 1 && (
          <NarrowSelect
            value={edition}
            aria-label="Редакция"
            onChange={(e) => {
              setEdition(e.target.value);
              setPage(0);
            }}
          >
            <option value="">Действующая редакция</option>
            {browsable
              .filter((e) => e.edition !== activeEdition)
              .map((e) => (
                <option key={e.id} value={e.edition}>
                  {e.title} ({editionStatusLabel(e.status).toLowerCase()})
                </option>
              ))}
          </NarrowSelect>
        )}
      </Controls>

      {!shownEdition && (
        <Note>
          Нормативная база ещё не загружена. Загрузите архив ФСНБ-2022 во вкладке «Редакции».
        </Note>
      )}
      {shownEdition && !ready && mode !== 'coefficients' && (
        <Note>
          Введите шифр или слова из наименования. Шифр можно писать с видом базы («ГЭСНр 57-2-1»)
          или без него, начало шифра тоже подойдёт — найдутся все нормы таблицы.
        </Note>
      )}
      {shownEdition && !ready && mode === 'coefficients' && (
        <Note>
          Оцифрованные поправочные коэффициенты ГГЭ к нормам ФСНБ-2022. Введите часть кода («3.230»)
          или слова условия («стеснённые», «зимнее время») либо выберите вид базы. Коэффициенты
          конкретной нормы — в её карточке.
        </Note>
      )}
      {ready && coefficientsMissing && (
        <Note>
          Поправочные коэффициенты в эту редакцию не загружены: она разобрана до появления
          коэффициентов в разделе.
        </Note>
      )}
      {ready && current.isFetching && !current.data && <Note>Ищем…</Note>}
      {ready && current.error && (
        <Err>{apiErrorText(current.error, 'Поиск не удался. Повторите запрос.')}</Err>
      )}
      {ready && current.data && totalFound === 0 && !coefficientsMissing && (
        <Note>Ничего не нашлось. Проверьте шифр или сократите запрос до одного-двух слов.</Note>
      )}

      {ready && mode === 'works' && works.data && works.data.items.length > 0 && (
        <TableWrap>
          <Grid>
            <thead>
              <tr>
                <th>Шифр</th>
                <th>Наименование</th>
                <th>Измеритель</th>
              </tr>
            </thead>
            <tbody>
              {works.data.items.map((w) => (
                <tr
                  key={`${w.base_type}-${w.code}`}
                  onClick={() => setOpenWork({ baseType: w.base_type, code: w.code })}
                >
                  <td className="code">{fullCode(w.base_type, w.code)}</td>
                  <td>{w.name}</td>
                  <td>{w.unit ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </Grid>
        </TableWrap>
      )}

      {ready && mode === 'resources' && resources.data && resources.data.items.length > 0 && (
        <TableWrap>
          <Grid>
            <thead>
              <tr>
                <th>Шифр</th>
                <th>Наименование</th>
                <th>Ед. изм.</th>
                <th>Цена</th>
                <th>Сборник</th>
              </tr>
            </thead>
            <tbody>
              {resources.data.items.map((r) => (
                <tr
                  key={`${r.source}-${r.code}`}
                  onClick={() => setOpenResource({ source: r.source, code: r.code })}
                >
                  <td className="code">{r.code}</td>
                  <td>{r.name}</td>
                  <td>{r.unit ?? '—'}</td>
                  <td>{priceText(r.cost, r.price_details)}</td>
                  <td>{sourceLabel(r.source)}</td>
                </tr>
              ))}
            </tbody>
          </Grid>
        </TableWrap>
      )}

      {ready &&
        mode === 'coefficients' &&
        coefficients.data &&
        coefficients.data.items.length > 0 && (
          <TableWrap>
            <Grid>
              <thead>
                <tr>
                  <th>Код</th>
                  <th>Условие применения</th>
                  <th>Техническая часть</th>
                </tr>
              </thead>
              <tbody>
                {coefficients.data.items.map((c) => (
                  <tr key={c.code} onClick={() => setOpenCoefficient(c.code)}>
                    <td className="code">{c.code}</td>
                    <td>{c.name}</td>
                    <td>
                      {c.section_name ?? '—'}
                      {c.decree ? ` · ${coefDecreeText(c.decree)}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Grid>
          </TableWrap>
        )}

      {ready && totalFound > 0 && (
        <Footer>
          <span className="grow">
            Найдено: {fmtNum(totalFound)}
            {editionTitle ? ` · ${editionTitle}` : ''}
          </span>
          {pages > 1 && (
            <>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Назад
              </Button>
              <span>
                {page + 1} из {fmtNum(pages)}
              </span>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                disabled={page + 1 >= pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Дальше
              </Button>
            </>
          )}
        </Footer>
      )}

      {openWork && shownEdition && (
        <WorkCardDrawer
          baseType={openWork.baseType}
          code={openWork.code}
          edition={shownEdition}
          editionTitle={editionTitle}
          onClose={() => setOpenWork(null)}
          onOpenResource={(source, code) => {
            setOpenWork(null);
            setOpenResource({ source, code });
          }}
          onOpenCoefficient={(code) => {
            setOpenWork(null);
            setOpenCoefficient(code);
          }}
        />
      )}
      {openCoefficient && shownEdition && (
        <CoefficientCardDrawer
          code={openCoefficient}
          edition={shownEdition}
          onClose={() => setOpenCoefficient(null)}
          onOpenWork={(baseType, code) => {
            setOpenCoefficient(null);
            setOpenWork({ baseType, code });
          }}
        />
      )}
      {openResource && shownEdition && (
        <ResourceCardDrawer
          source={openResource.source}
          code={openResource.code}
          edition={shownEdition}
          onClose={() => setOpenResource(null)}
          onOpenWork={(baseType, code) => {
            setOpenResource(null);
            setOpenWork({ baseType, code });
          }}
        />
      )}
    </Card>
  );
}
